import * as Sentry from '@sentry/browser';
import { of, throwError, timer } from 'rxjs';
import { catchError, map, retry, switchMap, tap } from 'rxjs/operators';
import { CookiesStorageService } from '@sp-core/services/cookies/cookies-storage.service';
import { TokenService } from '@sp-core/services/token/token.service';
import { UserService } from '@sp-core/services/user/user.service';
import { PermissionsService } from '@sp-core/services/permissions/permissions.service';
import { AuthHttpAdapter } from '@sp-core/http-adapters/auth-http-adapter';
import { AuthHttpService } from '@sp-core/http-services/auth-http.service';
import { ServerErrorModel } from '@sp-core/models/app-models/server-error.model';
import { buildServerErrorReport } from '@sp-core/services/sentry/server-error-report';

type SessionRestoreStage = 'profile' | 'permissions';

// A request that got no response at all (status 0) is usually a short network drop at page load.
// Without a second try the user is logged out and has to type the password and 2FA code again.
const PROFILE_RETRY_DELAY_MS = 1000;

// Only what a developer can act on goes to Sentry: a server failure or an error in our code.
// No response, 4xx and every role permissions failure stay as a breadcrumb - the device was
// offline or the session ended, and updateRole already reports its failures as "Role switch failed".
function reportSessionRestoreFailure(stage: SessionRestoreStage, error: unknown): void {
  const tags = { 'session_restore.stage': stage };

  if (stage === 'profile' && !(error instanceof ServerErrorModel)) {
    Sentry.captureException(error, { tags });
    return;
  }

  if (stage === 'profile' && error instanceof ServerErrorModel && error.status >= 500) {
    const { message, tags: reportTags, ...context } = buildServerErrorReport(error);
    Sentry.captureMessage(message, { ...context, tags: { ...reportTags, ...tags } });
    return;
  }

  Sentry.addBreadcrumb({
    category: 'session',
    level: 'warning',
    message: 'Session restore failed',
    data: { stage, status: (error as { status?: number })?.status ?? null },
  });
}

export function initSession(
  cookiesStorage: CookiesStorageService,
  tokenService: TokenService,
  userService: UserService,
  authHttpAdapter: AuthHttpAdapter,
  authHttp: AuthHttpService,
  permissionsService: PermissionsService,
): () => Promise<IUser | void | IRole> {
  return (): Promise<IUser | void | IRole> => {
    const { token } = cookiesStorage;
    tokenService.accessToken = token;
    if (token) {
      let stage: SessionRestoreStage = 'profile';
      return authHttp
        .getCurrentUser()
        .pipe(
          retry({
            count: 1,
            delay: (error) => (error?.status === 0 ? timer(PROFILE_RETRY_DELAY_MS) : throwError(() => error)),
          }),
          map((user) => authHttpAdapter.transformToUser(user)),
          tap((user) => {
            userService.user = user;
          }),
          // No retry here: updateRole reports every failure itself ("Role switch failed"), and the
          // endpoint registers the device on the backend, so a repeat is not free.
          switchMap((user) => {
            const role: IRole = (cookiesStorage.role as IRole) || (user.isMultipleRoles ? null : user.roles[0]);
            if (!role) {
              return of(null);
            }
            // Set only now, so that a failure while choosing the role is still reported as ours.
            stage = 'permissions';
            return permissionsService.updateRole(role);
          }),
          // Session restore runs inside APP_INITIALIZER, so anything thrown here stops Angular
          // from bootstrapping and the user gets a blank page instead of the login form. Failing
          // to restore a session means "ask the user to log in again", never "show nothing" - the
          // guards take it from here. A 401 never gets here: AuthInterceptor answers it with null,
          // which HttpClient drops, so the request completes without a value.
          catchError((error) => {
            reportSessionRestoreFailure(stage, error);

            return of(null);
          }),
        )
        .toPromise();
    }
    return new Promise<IUser | void | IRole>((resolve) => resolve());
  };
}
